import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { fuseAnimations } from '../../../../../../../core/animations';
import { SweetAlertService } from '../../../../../../services/sweet-alert/sweet-alert.service';
import { DivisionService } from '../../../../../../services/divisions/division.service';
import { SubjectService } from '../../../../../../services/subject/subject.service';
import { SchoolYearService } from '../../../../../../services/schoool-year/school-year.service';
import { ReportService } from '../../../../../../services/reports/report.service';
import { environment } from '../../../../../../../../environments/environment';
import { Observable } from 'rxjs/Observable';
import { EmployeeService } from '../../../../../../services/employee/employee.service';

@Component({
    selector: 'fuse-employee-schedule-dialog',
    templateUrl: './employee-schedule-dialog.component.html',
    styleUrls: ['./employee-schedule-dialog.component.scss'],
    animations: fuseAnimations
})
export class EmployeeScheduleDialogComponent implements OnInit {
    @BlockUI('schedule-block') blockUI: NgBlockUI;

    divisions: any[] = [];
    subjects: any[] = [];
    shifts: any[] = [];
    allAssignments: any[] = [];
    selectedShift: any;
    selectedDivision: any;
    selectedSubject: any;
    addedItems: any[] = [];
    currentSchoolYearId: number;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        public dialogRef: MatDialogRef<EmployeeScheduleDialogComponent>,
        private sweetAlertService: SweetAlertService,
        private divisionService: DivisionService,
        private subjectService: SubjectService,
        private schoolYearService: SchoolYearService,
        private reportService: ReportService,
        private employeeService: EmployeeService
    ) {
    }

    ngOnInit(): void {
        this.loadData();
    }

    loadData(): void {
        this.blockUI.start('Cargando datos...');
        this.schoolYearService.getAll().subscribe(schoolYears => {
            const activeYear = schoolYears.find(sy => sy.SchoolYearState.Id === 2);
            if (activeYear) {
                this.currentSchoolYearId = activeYear.Id;

                // Fetch assignments first
                this.employeeService.getAssignments(this.data.row.Id, this.currentSchoolYearId).subscribe(assignments => {
                    this.allAssignments = assignments;

                    // Filter shifts based on assignments
                    const uniqueShiftIds = Array.from(new Set(assignments.map(a => a.ShiftId)));
                    this.shifts = uniqueShiftIds.map(id => {
                        const a = assignments.find(x => x.ShiftId === id);
                        return {
                            Id: a.ShiftId,
                            Description: a.ShiftName
                        };
                    });

                    this.blockUI.stop();
                    if (this.shifts.length === 0) {
                        this.sweetAlertService.warning('Atención', 'El docente no tiene materias asignadas para el año escolar actual.', null);
                    }
                }, error => {
                    this.blockUI.stop();
                    this.sweetAlertService.error('Error', 'No se pudieron cargar las asignaciones del docente.');
                });
            } else {
                this.blockUI.stop();
                this.sweetAlertService.error('Error', 'No se encontró un año escolar activo.');
            }
        }, error => {
            this.blockUI.stop();
            this.sweetAlertService.error('Error', 'No se pudieron cargar los años escolares.');
        });
    }

    onShiftChange(shift: any): void {
        this.selectedDivision = null;
        this.selectedSubject = null;
        this.divisions = [];
        this.subjects = [];

        if (shift) {
            const uniqueDivisionIds = Array.from(new Set(
                this.allAssignments
                    .filter(a => a.ShiftId === shift.Id)
                    .map(a => a.DivisionId)
            ));

            this.divisions = uniqueDivisionIds.map(id => {
                const a = this.allAssignments.find(x => x.DivisionId === id);
                return {
                    Id: a.DivisionId,
                    FullName: a.DivisionName
                };
            });
        }
    }

    onDivisionChange(division: any): void {
        this.selectedSubject = null;
        this.subjects = [];
        if (division) {
            // Filter subjects based on assignments for this division
            this.subjects = this.allAssignments
                .filter(a => a.DivisionId === division.Id)
                .map(a => ({
                    SubjectId: a.SubjectId,
                    SubjectName: a.SubjectName
                }));
        }
    }

    addSubject(): void {
        if (!this.selectedDivision || !this.selectedSubject) return;

        const exists = this.addedItems.find(item =>
            item.division.Id === this.selectedDivision.Id &&
            item.subject.SubjectId === this.selectedSubject.SubjectId
        );

        if (exists) {
            this.sweetAlertService.warning('Atención', 'Esta materia ya fue agregada para este curso.', null);
            return;
        }

        this.addedItems.push({
            division: {
                Id: this.selectedDivision.Id,
                FullName: this.selectedDivision.FullName
            },
            subject: {
                SubjectId: this.selectedSubject.SubjectId,
                SubjectName: this.selectedSubject.SubjectName
            },
            shiftId: this.selectedShift.Id,
            shiftName: this.selectedShift.Description
        });

        // Reset selections
        // this.selectedDivision = null;
        this.selectedSubject = null;
    }

    addAllAssignments(): void {
        const assignmentsToAdd = this.selectedShift
            ? this.allAssignments.filter(a => a.ShiftId === this.selectedShift.Id)
            : this.allAssignments;

        assignmentsToAdd.forEach(assignment => {
            const exists = this.addedItems.find(item =>
                item.division.Id === assignment.DivisionId &&
                item.subject.SubjectId === assignment.SubjectId
            );

            if (!exists) {
                this.addedItems.push({
                    division: {
                        Id: assignment.DivisionId,
                        FullName: assignment.DivisionName
                    },
                    subject: {
                        SubjectId: assignment.SubjectId,
                        SubjectName: assignment.SubjectName
                    },
                    shiftId: assignment.ShiftId,
                    shiftName: assignment.ShiftName
                });
            }
        });
    }

    removeItem(index: number): void {
        this.addedItems.splice(index, 1);
    }

    generateSchedule(): void {
        if (this.addedItems.length === 0) return;

        const items = this.addedItems.map(item => ({
            DivisionId: item.division.Id,
            SubjectId: item.subject.SubjectId
        }));

        const payload = {
            EmployeeId: this.data.row.Id,
            Items: items
        };

        this.blockUI.start('Generando horario...');

        this.reportService.teacherSubjectSchedule(payload).subscribe(blob => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const timestamp = new Date().getTime();
            link.download = `Horario_${this.data.row.LastName}_${timestamp}.pdf`;
            link.click();
            window.URL.revokeObjectURL(url);
            this.blockUI.stop();
            this.dialogRef.close();
        }, error => {
            this.blockUI.stop();
            this.sweetAlertService.error('Error', 'No se pudo generar el horario. Verifique que las materias tengan horarios asignados.', null);
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/main/content/pages/management/employees/components/employee-schedule-dialog/employee-schedule-dialog.component.ts